import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { scoutClubInterestService } from '../../../services/adminUserManagementService';
import { useAuth } from '../../../contexts/AuthContext';
import { performanceStatsService } from '../../../services/playerMasterService';
import Input from '../../../components/ui/Input';

// Mini EA Sports FC badge
const EABadge = () => (
  <div className="flex items-center px-2 py-0.5 rounded" style={{ background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.4)' }}>
    <svg viewBox="0 0 80 22" width="64" height="18">
      <text x="0" y="9" fontFamily="Arial Black, sans-serif" fontSize="6" fontWeight="900" fill="#FF6B00" letterSpacing="0.8">EA SPORTS</text>
      <text x="0" y="20" fontFamily="Arial Black, sans-serif" fontSize="12" fontWeight="900" fill="white">FC</text>
      <rect x="16" y="12" width="64" height="1.5" fill="#FF6B00"/>
      <text x="18" y="20" fontFamily="Arial, sans-serif" fontSize="6" fontWeight="700" fill="#FF6B00" letterSpacing="1.5">24/25</text>
    </svg>
  </div>
);

// Mini PS5 badge
const PS5Badge = () => (
  <div className="flex items-center px-2 py-0.5 rounded" style={{ background: 'rgba(0,52,143,0.2)', border: '1px solid rgba(0,100,255,0.4)' }}>
    <svg viewBox="0 0 36 16" width="30" height="13">
      <text x="0" y="13" fontFamily="Arial Black, sans-serif" fontSize="13" fontWeight="900" fill="white" letterSpacing="-0.5">PS5</text>
    </svg>
  </div>
);

const PlayerCard = ({ player, onClick, onCompare, isInComparison, isSelected = false, bulkSelectMode = false }) => {
  const { hasAnyRole } = useAuth();
  const canExpressInterest = hasAnyRole(['club', 'scout']);
  const [expressingInterest, setExpressingInterest] = useState(false);
  const [interestExpressed, setInterestExpressed] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestDescription, setInterestDescription] = useState('');
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [isEditingStats, setIsEditingStats] = useState(false);
  const [editedStats, setEditedStats] = useState({});
  const [savingStats, setSavingStats] = useState(false);

  const handleExpressInterest = async () => {
    if (!interestDescription?.trim()) {
      alert('Please provide a description of your interest');
      return;
    }
    setExpressingInterest(true);
    try {
      await scoutClubInterestService?.expressInterest(player?.id, interestDescription);
      setInterestExpressed(true);
      setShowInterestModal(false);
      setInterestDescription('');
      setTimeout(() => setInterestExpressed(false), 3000);
    } catch (error) {
      console.error('Error expressing interest:', error);
      alert('Failed to express interest. Please try again.');
    } finally {
      setExpressingInterest(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) age--;
    return age;
  };

  const formatPosition = (position) => {
    if (!position) return 'N/A';
    return position?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const careerStats = {
    matches: player?.totalAppearances || player?.total_appearances || 0,
    goals: player?.goalsScored || player?.goals_scored || 0,
    assists: player?.assists || 0,
    rating: player?.averageRating || player?.average_rating || 7.5
  };

  // Render star rating (out of 10, show 5 stars)
  const renderStars = (rating) => {
    const numRating = parseFloat(rating) || 0;
    const stars = Math.round((numRating / 10) * 5); // convert 0-10 to 0-5 stars
    return (
      <div className="flex items-center gap-0.5">
        {[1,2,3,4,5]?.map(i => (
          <svg key={i} viewBox="0 0 12 12" width="12" height="12">
            <polygon
              points="6,1 7.5,4.5 11,4.5 8.5,7 9.5,11 6,9 2.5,11 3.5,7 1,4.5 4.5,4.5"
              fill={i <= stars ? '#FFB800' : 'rgba(255,255,255,0.15)'}
            />
          </svg>
        ))}
        <span className="text-xs font-bold ml-1" style={{ color: '#FFB800' }}>{numRating?.toFixed ? numRating?.toFixed(1) : numRating}</span>
      </div>
    );
  };

  const handleStartEdit = () => {
    setEditedStats({ matches: careerStats?.matches, goals: careerStats?.goals, assists: careerStats?.assists, rating: careerStats?.rating });
    setIsEditingStats(true);
  };

  const handleSaveStats = async () => {
    if (!player?.id) return;
    setSavingStats(true);
    try {
      const statsResponse = await performanceStatsService?.getByPlayerId(player?.id);
      const existingStats = statsResponse?.data || [];
      if (existingStats?.length === 0) {
        await performanceStatsService?.create({
          playerId: player?.id, season: 'Career Total', competitionType: 'All Competitions',
          appearances: parseInt(editedStats?.matches) || 0, goalsScored: parseInt(editedStats?.goals) || 0,
          assists: parseInt(editedStats?.assists) || 0, motmAwards: parseFloat(editedStats?.rating) || 7.5
        });
      } else {
        const firstStat = existingStats?.[0];
        await performanceStatsService?.update(firstStat?.id, {
          appearances: parseInt(editedStats?.matches) || 0, goalsScored: parseInt(editedStats?.goals) || 0,
          assists: parseInt(editedStats?.assists) || 0, motmAwards: parseFloat(editedStats?.rating) || 7.5
        });
      }
      player.totalAppearances = parseInt(editedStats?.matches) || 0;
      player.goalsScored = parseInt(editedStats?.goals) || 0;
      player.assists = parseInt(editedStats?.assists) || 0;
      player.averageRating = parseFloat(editedStats?.rating) || 7.5;
      setIsEditingStats(false);
      alert('Career stats updated successfully!');
    } catch (error) {
      console.error('Error saving career stats:', error);
      alert('Failed to save career stats. Please try again.');
    } finally {
      setSavingStats(false);
    }
  };

  const handleCancelEdit = () => { setIsEditingStats(false); setEditedStats({}); };
  const handleStatChange = (field, value) => { setEditedStats(prev => ({ ...prev, [field]: value })); };

  const careerHighlights = [];
  if (player?.trophies) careerHighlights?.push(player?.trophies);
  if (player?.competitions) careerHighlights?.push(player?.competitions);
  if (player?.nationalTeam || player?.national_team) {
    careerHighlights?.push(`${player?.nationalityPrimary || player?.nationality} International`);
  }
  if (careerStats?.goals > 100) careerHighlights?.push(`${careerStats?.goals}+ Career Goals`);

  const positionColorMap = {
    'forward': '#FF6B00', 'striker': '#FF6B00', 'winger': '#FF9500',
    'midfielder': '#0078ff', 'defensive-midfielder': '#0050cc', 'attacking-midfielder': '#0099ff',
    'defender': '#00a651', 'centre-back': '#007a3d', 'full-back': '#00c060',
    'goalkeeper': '#8b00ff'
  };
  const posRaw = (player?.primaryPosition || player?.position || '')?.toLowerCase();
  const posColor = Object.entries(positionColorMap)?.find(([k]) => posRaw?.includes(k))?.[1] || '#0078ff';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.01] hover:shadow-2xl ${
        isSelected && bulkSelectMode ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-transparent' : ''
      }`}
      style={{
        background: 'linear-gradient(135deg, #0d1117 0%, #161b27 50%, #0d1a0d 100%)',
        border: '1px solid rgba(255,255,255,0.07)',
        boxShadow: '0 4px 30px rgba(0,0,0,0.5)'
      }}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${posColor}, transparent)` }} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Left Side - Player Image */}
        <div
          className="relative aspect-[3/4] lg:aspect-auto overflow-hidden cursor-pointer"
          style={{ background: 'linear-gradient(135deg, #0a0a1a, #1a1a2e)', minHeight: '280px' }}
          onClick={onClick}
        >
          <Image
            src={player?.profilePhotoUrl || player?.player_photo || '/assets/images/no_image.png'}
            alt={`${player?.name || player?.fullName || player?.full_name} profile photo`}
            className="w-full h-full object-cover"
          />
          {/* Multi-layer gradient overlay */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(13,17,23,0.97) 0%, rgba(13,17,23,0.35) 55%, rgba(13,17,23,0.05) 100%)' }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${posColor}18 0%, transparent 55%)` }} />

          {/* Market Value Badge */}
          <div className="absolute top-4 left-4 rounded-xl px-3 py-2" style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div className="text-xs font-semibold mb-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Market Value</div>
            <div className="text-base font-black" style={{ color: '#00d68f' }}>
              &euro;{player?.currentMarketValue || player?.current_market_value || player?.market_value || '0.0'}M
            </div>
          </div>

          {/* Video Play Button */}
          {player?.matchFootage && (
            <button
              className="absolute top-4 right-4 w-10 h-10 rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-300"
              style={{ background: `linear-gradient(135deg, ${posColor}, ${posColor}cc)`, boxShadow: `0 0 20px ${posColor}50` }}
            >
              <Icon name="Play" size={18} color="white" />
            </button>
          )}

          {/* Player Name & Info */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            {/* Mini partner badges */}
            <div className="flex gap-2 mb-3">
              <EABadge />
              <PS5Badge />
            </div>
            <h3 className="text-xl md:text-2xl font-black text-white mb-2" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.9)' }}>
              {player?.fullName || player?.full_name || player?.name}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(5px)' }}>
                <Icon name="MapPin" size={12} color="rgba(255,255,255,0.7)" />
                <span className="font-semibold text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{player?.nationalityPrimary || player?.nationality || 'N/A'}</span>
              </div>
              {calculateAge(player?.dateOfBirth || player?.date_of_birth) && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(5px)' }}>
                  <Icon name="Calendar" size={12} color="rgba(255,255,255,0.7)" />
                  <span className="font-semibold text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{calculateAge(player?.dateOfBirth || player?.date_of_birth)} yrs</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Stats & Info */}
        <div className="p-5 space-y-4">
          {/* Position & Current Club */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl p-3" style={{ background: `${posColor}18`, border: `1px solid ${posColor}35` }}>
              <div className="text-xs font-bold mb-1" style={{ color: posColor }}>Position</div>
              <div className="text-sm font-black text-white">{formatPosition(player?.primaryPosition || player?.position)}</div>
            </div>
            <div className="rounded-xl p-3" style={{ background: 'rgba(0,214,143,0.1)', border: '1px solid rgba(0,214,143,0.28)' }}>
              <div className="text-xs font-bold mb-1" style={{ color: '#00d68f' }}>Current Club</div>
              <div className="text-sm font-black text-white truncate">{player?.currentClub || player?.current_club || player?.current_team || 'Free Agent'}</div>
            </div>
          </div>

          {/* Key Stats */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" size={15} color="rgba(255,255,255,0.5)" />
                <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.6)' }}>Career Stats</h4>
              </div>
              {isAdmin && !isEditingStats && (
                <button onClick={handleStartEdit} className="text-xs font-semibold flex items-center gap-1" style={{ color: posColor }}>
                  <Icon name="Edit" size={11} />
                  Edit
                </button>
              )}
            </div>
            {isEditingStats ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {[['matches','Matches','#0078ff'],['goals','Goals','#FF6B00'],['assists','Assists','#8b00ff'],['rating','Rating','#FFB800']]?.map(([field, label, color]) => (
                    <div key={field} className="rounded-lg p-2.5" style={{ background: `${color}15`, border: `1px solid ${color}35` }}>
                      <div className="text-xs font-bold mb-1" style={{ color }}>{label}</div>
                      <Input
                        type="number"
                        step={field === 'rating' ? '0.1' : '1'}
                        min="0"
                        max={field === 'rating' ? '10' : undefined}
                        value={editedStats?.[field]}
                        onChange={(e) => handleStatChange(field, e?.target?.value)}
                        className="text-center text-base font-black bg-transparent border-0 text-white p-0 h-auto"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSaveStats} disabled={savingStats}
                    className="flex-1 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90"
                    style={{ background: posColor }}>
                    {savingStats ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancelEdit} disabled={savingStats}
                    className="flex-1 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-90"
                    style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {[
                  { val: careerStats?.matches, label: 'Apps', color: '#0078ff' },
                  { val: careerStats?.goals, label: 'Goals', color: '#FF6B00' },
                  { val: careerStats?.assists, label: 'Assists', color: '#8b00ff' },
                  { val: typeof careerStats?.rating === 'number' ? careerStats?.rating?.toFixed(1) : careerStats?.rating, label: 'Rating', color: '#FFB800' }
                ]?.map((s, i) => (
                  <div key={i} className="rounded-xl p-2.5 text-center" style={{ background: `${s?.color}14`, border: `1px solid ${s?.color}28` }}>
                    {i === 3 ? (
                      <>
                        <div className="flex justify-center mb-0.5">{renderStars(s?.val)}</div>
                      </>
                    ) : (
                      <div className="text-lg font-black" style={{ color: s?.color }}>{s?.val}</div>
                    )}
                    <div className="text-xs font-semibold mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>{s?.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Career Highlights */}
          {careerHighlights?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Trophy" size={14} color="#FFB800" />
                <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>Highlights</h4>
              </div>
              <div className="space-y-1.5">
                {careerHighlights?.slice(0, 3)?.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2 text-xs">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(255,184,0,0.15)' }}>
                      <Icon name="Award" size={9} color="#FFB800" />
                    </div>
                    <span className="leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={(e) => { e?.stopPropagation(); onClick?.(); }}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 hover:opacity-90"
              style={{ background: `linear-gradient(135deg, ${posColor}, ${posColor}cc)`, boxShadow: `0 4px 15px ${posColor}35` }}
            >
              View Profile
            </button>
            {canExpressInterest && (
              <button
                onClick={(e) => { e?.stopPropagation(); setShowInterestModal(true); }}
                disabled={expressingInterest || interestExpressed}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 hover:opacity-90"
                style={{
                  background: interestExpressed ? 'rgba(0,214,143,0.15)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${interestExpressed ? 'rgba(0,214,143,0.4)' : 'rgba(255,255,255,0.12)'}`,
                  color: interestExpressed ? '#00d68f' : 'rgba(255,255,255,0.8)'
                }}
              >
                {interestExpressed ? '\u2713 Interested' : '\u2665 Interest'}
              </button>
            )}
            <button
              onClick={(e) => { e?.stopPropagation(); onCompare?.(); }}
              className="px-3 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 hover:opacity-90"
              style={{
                background: isInComparison ? 'rgba(255,107,0,0.18)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${isInComparison ? 'rgba(255,107,0,0.45)' : 'rgba(255,255,255,0.1)'}`,
                color: isInComparison ? '#FF6B00' : 'rgba(255,255,255,0.5)'
              }}
            >
              <Icon name={isInComparison ? 'X' : 'GitCompare'} size={15} />
            </button>
          </div>
        </div>
      </div>
      {/* Interest Modal */}
      {showInterestModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={(e) => { e?.stopPropagation(); setShowInterestModal(false); }}
        >
          <div
            className="rounded-2xl p-6 max-w-md w-full"
            style={{ background: '#161b27', border: '1px solid rgba(255,255,255,0.1)' }}
            onClick={(e) => e?.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-4">
              Express Interest in {player?.fullName || player?.full_name}
            </h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.45)' }}>Please describe why you&apos;re interested in this player:</p>
            <textarea
              className="w-full px-4 py-3 rounded-xl text-white resize-none focus:outline-none focus:ring-1 focus:ring-orange-500"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}
              rows={4}
              placeholder="E.g., Looking for a striker for our youth academy..."
              value={interestDescription}
              onChange={(e) => setInterestDescription(e?.target?.value)}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={(e) => { e?.stopPropagation(); setShowInterestModal(false); setInterestDescription(''); }}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm"
                style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.65)' }}
              >Cancel</button>
              <button
                onClick={(e) => { e?.stopPropagation(); handleExpressInterest(); }}
                disabled={expressingInterest || !interestDescription?.trim()}
                className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm"
                style={{ background: 'linear-gradient(135deg, #FF6B00, #FF9500)' }}
              >
                {expressingInterest ? 'Sending...' : 'Submit Interest'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerCard;